'use client';
import { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter, useSearchParams } from 'next/navigation';
import {
  Box,
  TextField,
  Button,
  Typography,
  Stack,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  Fade,
  Slide,
  Grow,
  alpha,
  useMediaQuery,
  useTheme,
  Grid,
  Chip,
} from '@mui/material';
import {
  Clear,
  ArrowForward,
  ArrowBack,
  CheckCircle,
  LocalShipping,
  Speed,
  Security,
  SupportAgent,
  LocationOn,
  Verified,
} from '@mui/icons-material';
import { login } from '../redux/slices/authSlice';
import { userProfileRequestSuccess } from '../redux/slices/userProfileSlice';
import { formatApiError } from '../../services/api/apiHelpers';
import useApiCall from '../../services/api/useApiCall';
import { buildApiConfig } from '../../services/api/apiClient';
import { endPointEnums } from '@/services/api/endpoints';
import SimpleNav from '@/components/SimpleNav';
import { useTranslation } from '@/kit/useTranslation';
import { useFirebasePhoneAuth } from '@/hooks/useFirebasePhoneAuth';
import { brandingConfig } from '@/config/branding';

// Feature cards data
const features = [
  {
    icon: Speed,
    titleKey: 'auth.features.fastDelivery',
    descKey: 'auth.features.fastDeliveryDesc',
  },
  {
    icon: LocationOn,
    titleKey: 'auth.features.liveTracking',
    descKey: 'auth.features.liveTrackingDesc',
  },
  { icon: Security, titleKey: 'auth.features.secure', descKey: 'auth.features.secureDesc' },
  {
    icon: SupportAgent,
    titleKey: 'auth.features.support247',
    descKey: 'auth.features.support247Desc',
  },
];

// Stats for desktop view
const stats = [
  { value: '200+', labelKey: 'auth.stats.deliveries' },
  { value: '98%', labelKey: 'auth.stats.successRate' },
  { value: '15min', labelKey: 'auth.stats.avgPickup' },
  { value: '4.9★', labelKey: 'auth.stats.rating' },
];

const Login = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { t } = useTranslation();
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [step, setStep] = useState('phone'); // 'phone' | 'otp' | 'success'
  const [isNavigating, setIsNavigating] = useState(false); // Prevent form flash during navigation
  const [resendTimer, setResendTimer] = useState(60);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const dispatch = useDispatch();
  const router = useRouter();
  const searchParams = useSearchParams();
  const otpRefs = useRef([]);

  // Redirect if already logged in (e.g. user bookmarked /login while having a session)
  useEffect(() => {
    if (isLoggedIn && !isNavigating) {
      const requestedRedirect = searchParams?.get('redirect') || '/home';
      setIsNavigating(true);
      router.push(requestedRedirect);
    }
  }, [isLoggedIn, router, searchParams, isNavigating]);

  // Timer effect for OTP resend
  useEffect(() => {
    let interval = null;
    if (isTimerActive && resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    } else if (resendTimer === 0) {
      setIsTimerActive(false);
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isTimerActive, resendTimer]);

  // Firebase phone auth hook
  const {
    loading: firebaseLoading,
    error: firebaseError,
    sendOTP: sendFirebaseOTP,
    verifyOTP: verifyFirebaseOTP,
    resetAuth: resetFirebaseAuth,
  } = useFirebasePhoneAuth();

  // API call for verifying Firebase token with backend
  const {
    loading: backendVerifyLoading,
    error: backendVerifyError,
    makeApiCall: verifyWithBackend,
  } = useApiCall();

  const [authError, setAuthError] = useState(null);
  const [otpMode, setOtpMode] = useState(null); // 'firebase' | 'bypass' | null
  const [showAdminBypassOption, setShowAdminBypassOption] = useState(false); // Show explicit bypass button when OTP send fails

  // If we somehow end up on OTP step without a mode, reset to phone input.
  useEffect(() => {
    if (step === 'otp' && !otpMode) {
      setStep('phone');
    }
  }, [step, otpMode]);

  const checkBypassEnabledForPhone = async () => {
    if (process.env.NEXT_PUBLIC_ADMIN_BYPASS_ENABLED !== 'true') return false;
    try {
      const config = buildApiConfig(endPointEnums.CHECK_ADMIN_BYPASS, {
        phoneNumber: `+91${phone}`,
        portal: 'L',
      });
      const res = await verifyWithBackend(config);
      console.log('[Login] Bypass check response:', res);
      return !!(res?.success && (res?.data?.isBypass || res?.data?.allowed));
    } catch (e) {
      console.error('[Login] Bypass check error:', e);
      return false;
    }
  };

  const handleSendOTP = async () => {
    if (isPhoneValid) {
      setAuthError(null);
      setShowAdminBypassOption(false);
      setOtpMode(null);

      const bypassAllowed = await checkBypassEnabledForPhone();
      if (bypassAllowed) {
        setOtpMode('bypass');
        setStep('otp');
        return;
      }

      const result = await sendFirebaseOTP(phone, 'recaptcha-container');

      if (result.success) {
        setOtpMode('firebase');
        setStep('otp');
        setResendTimer(60);
        setIsTimerActive(true);
      } else {
        if (process.env.NEXT_PUBLIC_ADMIN_BYPASS_ENABLED === 'true') {
          // Offer the user to use the admin bypass option instead of auto-advancing.
          setShowAdminBypassOption(true);
          setAuthError(result.error || 'Failed to send OTP. You can try the admin bypass option.');
        } else {
          setAuthError(result.error || 'Failed to send OTP');
        }
      }
    }
  };

  const handleResendOTP = async () => {
    if (isTimerActive) return;
    setAuthError(null);
    setIsResending(true);
    try {
      const result = await sendFirebaseOTP(phone, 'recaptcha-container');
      if (result.success) {
        // Reset any stale bypass state when successful OTP sent
        setOtpMode('firebase');
        setResendTimer(60);
        setIsTimerActive(true);
      } else {
        setAuthError(result.error || 'Failed to resend OTP');
      }
    } finally {
      setIsResending(false);
    }
  };

  const handleChangePhone = () => {
    setStep('phone');
    setOtp(['', '', '', '', '', '']);
    setIsTimerActive(false);
    setOtpMode(null);
    setShowAdminBypassOption(false);
  };

  /**
   * Try admin bypass authentication.
   * Returns the API result on success, or null on failure.
   */
  const tryAdminBypass = async (otpString) => {
    try {
      const apiObject = buildApiConfig(endPointEnums.ADMIN_BYPASS, {
        phoneNumber: `+91${phone}`,
        pin: otpString,
        portal: 'L',
      });
      const result = await verifyWithBackend(apiObject);
      if (result && result.success) return result;
    } catch (e) {
      // Admin bypass failed — not an admin or wrong pin
    }
    return null;
  };

  const handleVerifyOTP = async () => {
    const otpString = otp.join('');
    if (otpString.length === 6) {
      setAuthError(null);

      // If we are in bypass mode, skip Firebase and authenticate using the admin PIN
      if (otpMode === 'bypass') {
        const adminResult = await tryAdminBypass(otpString);
        if (adminResult) {
          handleAuthSuccess(adminResult, `+91${phone}`);
          return;
        }
        setAuthError('Invalid access code');
        return;
      }

      // If we are not in a verified mode, stay on phone input (invalid state)
      if (otpMode !== 'firebase') {
        setAuthError('Please enter your phone number to continue');
        setStep('phone');
        return;
      }

      try {
        // Step 1: Verify OTP with Firebase
        const firebaseResult = await verifyFirebaseOTP(otpString);

        if (!firebaseResult.success) {
          // Firebase OTP failed — try admin bypass as silent fallback
          if (process.env.NEXT_PUBLIC_ADMIN_BYPASS_ENABLED === 'true') {
            const adminResult = await tryAdminBypass(otpString);
            if (adminResult) {
              handleAuthSuccess(adminResult, `+91${phone}`);
              return;
            }
          }
          setAuthError(firebaseResult.error || 'Invalid OTP');
          return;
        }

        // Step 2: Send Firebase ID token to backend for verification
        const { idToken, phoneNumber } = firebaseResult.user;

        const apiObject = buildApiConfig(endPointEnums.VERIFY_FIREBASE_TOKEN, {
          idToken,
          phoneNumber,
          portal: 'L',
        });

        const result = await verifyWithBackend(apiObject);
        if (result && result.success) {
          handleAuthSuccess(result, phoneNumber);
        } else {
          setAuthError('Failed to authenticate with server');
        }
      } catch (err) {
        console.error('OTP verification error:', err);
        // Last resort: try admin bypass on unexpected errors
        if (process.env.NEXT_PUBLIC_ADMIN_BYPASS_ENABLED === 'true') {
          const adminResult = await tryAdminBypass(otpString);
          if (adminResult) {
            handleAuthSuccess(adminResult, `+91${phone}`);
            return;
          }
        }
        setAuthError('Authentication failed. Please try again.');
      }
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);

    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }

    if (newOtp.every((d) => d) && newOtp.join('').length === 6) {
      setTimeout(() => handleVerifyOTP(), 100);
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
    // Submit on Enter if all digits are filled
    if (e.key === 'Enter' && otp.every((d) => d) && !isVerifying) {
      handleVerifyOTP();
    }
  };

  // Combined loading/error states for UI
  const isSending = firebaseLoading && step === 'phone';
  const isVerifying = firebaseLoading || backendVerifyLoading;
  const currentErrorMessage =
    authError || formatApiError(firebaseError) || formatApiError(backendVerifyError);

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newOtp = [...otp];
    for (let i = 0; i < pasted.length; i++) {
      newOtp[i] = pasted[i];
    }
    setOtp(newOtp);
    if (pasted.length === 6) {
      setTimeout(() => handleVerifyOTP(), 100);
    }
  };

  const isPhoneValid = /^\d{10}$/.test(phone);

  const handleAuthSuccess = (result, phoneNumber) => {
    // Backend response: { success, data: { token, user: {...} }, message }
    // The token is ALWAYS at result.data.token regardless of user type
    const token = result.data?.token;
    const apiUser = result.data?.user || {};

    const userData = {
      token,
      phoneNumber: phoneNumber || apiUser.userPhoneNumber || apiUser.phone,
      userId: apiUser.userId,
      userType: apiUser.userType || null,
      userName: apiUser.userName || apiUser.fullName || null,
      userEmail: apiUser.userEmail || apiUser.email || null,
      businessSetupCompleted: !!apiUser.businessSetupCompleted,
    };

    // Persist to localStorage for getJWT() and API authorization headers
    if (typeof window !== 'undefined') {
      localStorage.setItem('auth', JSON.stringify(userData));
    }

    // Update Redux — this also triggers redux-persist to save to persist:root
    dispatch(login(userData));

    // Populate userProfile slice so LayoutContainer shows navigation immediately
    dispatch(userProfileRequestSuccess({ user: apiUser }));

    setStep('success');

    // Navigate after success animation — short delay gives React time to commit
    // the success screen and lets redux-persist flush before navigation.
    let targetPath = '/home';
    if (!apiUser.userType) {
      targetPath = '/user-type';
    } else if (String(apiUser.userType).toLowerCase() === 'business') {
      targetPath = apiUser.businessSetupCompleted ? '/business-dashboard' : '/business-setup';
    }
    setTimeout(() => {
      setIsNavigating(true);
      router.push(targetPath);
    }, 500);
  };

  // Show success screen after auth — guard with isNavigating to
  // prevent the login form flashing while the router transition is in progress.
  // NOTE: do NOT include isLoggedIn here — that would show the loading screen on
  // every page reload for users with a persisted session, blocking navigation.
  // Already-logged-in users are redirected by the useEffect above.
  if (step === 'success' || isNavigating) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #000000 0%, #2d2d2d 100%)',
        }}
      >
        <Fade in timeout={300}>
          <Stack spacing={3} alignItems="center">
            <Box
              sx={{
                width: 100,
                height: 100,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(10px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'pulse 1.5s ease-in-out infinite',
                border: '2px solid rgba(255,255,255,0.3)',
                '@keyframes pulse': {
                  '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                  '50%': { transform: 'scale(1.05)', opacity: 0.9 },
                },
              }}
            >
              <CheckCircle sx={{ fontSize: 48, color: '#fff' }} />
            </Box>
            <Typography sx={{ fontSize: '1.2rem', fontWeight: 600, color: '#fff' }}>
              {t('auth.settingUp')}
            </Typography>
          </Stack>
        </Fade>
      </Box>
    );
  }

  // Desktop Layout
  if (isDesktop) {
    return (
      <>
        <SimpleNav />
        {/* reCAPTCHA container for Firebase Phone Auth (invisible) */}
        <div id="recaptcha-container"></div>
        <Box sx={{ minHeight: '100vh', display: 'flex', pt: 8 }}>
          {/* Left Panel - Branding & Features */}
          <Box
            sx={{
              width: '55%',
              background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #2d2d2d 100%)',
              position: 'relative',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              p: 6,
            }}
          >
            {/* Animated background shapes */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                overflow: 'hidden',
                pointerEvents: 'none',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: '10%',
                  left: '10%',
                  width: 400,
                  height: 400,
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)',
                  animation: 'float 8s ease-in-out infinite',
                  '@keyframes float': {
                    '0%, 100%': { transform: 'translate(0, 0)' },
                    '50%': { transform: 'translate(30px, -30px)' },
                  },
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: '5%',
                  right: '5%',
                  width: 300,
                  height: 300,
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)',
                  animation: 'float 10s ease-in-out infinite reverse',
                }}
              />
              {/* Delivery truck animation path */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: '20%',
                  animation: 'drive 15s linear infinite',
                  '@keyframes drive': {
                    '0%': { left: '-100px' },
                    '100%': { left: 'calc(100% + 100px)' },
                  },
                }}
              >
                <LocalShipping sx={{ fontSize: 60, color: 'rgba(255,255,255,0.15)' }} />
              </Box>
            </Box>

            {/* Content */}
            <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 520 }}>
              {/* Logo */}
              <Fade in timeout={800}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      position: 'relative',
                      borderRadius: 3,
                      bgcolor: 'rgba(255,255,255,0.15)',
                      backdropFilter: 'blur(10px)',
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Image
                      src="/assets/terrakite-logo.png"
                      alt={brandingConfig.brandName}
                      width={48}
                      height={48}
                      style={{ objectFit: 'contain' }}
                    />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: '2rem',
                        fontWeight: 800,
                        color: '#fff',
                        lineHeight: 1,
                      }}
                    >
                      {brandingConfig.brandName}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.9rem',
                        color: 'rgba(255,255,255,0.7)',
                      }}
                    >
                      {t('auth.deliveryPlatform')}
                    </Typography>
                  </Box>
                </Stack>
              </Fade>

              {/* Headline */}
              <Fade in timeout={1000}>
                <Box mb={4}>
                  <Typography
                    sx={{
                      fontSize: '3rem',
                      fontWeight: 800,
                      color: '#fff',
                      lineHeight: 1.15,
                      mb: 2,
                    }}
                  >
                    {t('auth.tagline')}
                    <br />
                    <Box component="span" sx={{ color: '#a7f3d0' }}>
                      {t('auth.taglineHighlight')}
                    </Box>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '1.1rem',
                      color: 'rgba(255,255,255,0.8)',
                      lineHeight: 1.6,
                      maxWidth: 440,
                    }}
                  >
                    {t('auth.taglineDesc')}
                  </Typography>
                </Box>
              </Fade>

              {/* Stats */}
              <Fade in timeout={1200}>
                <Stack direction="row" spacing={4} mb={5}>
                  {stats.map((stat, i) => (
                    <Box key={i}>
                      <Typography
                        sx={{
                          fontSize: '1.75rem',
                          fontWeight: 800,
                          color: '#fff',
                        }}
                      >
                        {stat.value}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.85rem',
                          color: 'rgba(255,255,255,0.6)',
                        }}
                      >
                        {t(stat.labelKey)}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Fade>

              {/* Feature Grid */}
              <Grid container spacing={2}>
                {features.map((feature, i) => (
                  <Grid item xs={6} key={i}>
                    <Grow in timeout={1000 + i * 200}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 3,
                          bgcolor: 'rgba(255,255,255,0.08)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            bgcolor: 'rgba(255,255,255,0.12)',
                            transform: 'translateY(-2px)',
                          },
                        }}
                      >
                        <feature.icon sx={{ fontSize: 28, color: '#a7f3d0', mb: 1 }} />
                        <Typography
                          sx={{
                            fontSize: '0.95rem',
                            fontWeight: 700,
                            color: '#fff',
                            mb: 0.25,
                          }}
                        >
                          {t(feature.titleKey)}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.8rem',
                            color: 'rgba(255,255,255,0.6)',
                          }}
                        >
                          {t(feature.descKey)}
                        </Typography>
                      </Box>
                    </Grow>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>

          {/* Right Panel - Login Form */}
          <Box
            sx={{
              width: '45%',
              bgcolor: '#fff',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              p: 6,
            }}
          >
            <Box sx={{ width: '100%', maxWidth: 400 }}>
              {/* Back button for OTP step */}
              {step === 'otp' && (
                <IconButton
                  onClick={() => {
                    setStep('phone');
                    setOtp(['', '', '', '', '', '']);
                  }}
                  sx={{ mb: 2, color: '#374151' }}
                >
                  <ArrowBack />
                </IconButton>
              )}

              <Slide direction="left" in={step === 'phone'} mountOnEnter unmountOnExit>
                <Box>
                  <Typography
                    sx={{
                      fontSize: '1.75rem',
                      fontWeight: 700,
                      color: '#0f172a',
                      mb: 1,
                    }}
                  >
                    {t('auth.welcomeBack')}
                  </Typography>
                  <Typography sx={{ fontSize: '1rem', color: '#6b7280', mb: 4 }}>
                    {t('auth.enterMobile')}
                  </Typography>

                  {/* Phone Input */}
                  <TextField
                    fullWidth
                    placeholder={t('auth.phonePlaceholder')}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && isPhoneValid && !isSending) {
                        handleSendOTP();
                      }
                    }}
                    inputProps={{
                      maxLength: 10,
                      inputMode: 'numeric',
                      style: {
                        fontSize: '1.15rem',
                        fontWeight: 500,
                        letterSpacing: 1,
                      },
                    }}
                    disabled={isSending}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              pr: 2,
                              borderRight: '1px solid #e5e7eb',
                            }}
                          >
                            <Typography sx={{ fontSize: '1.25rem' }}>🇮🇳</Typography>
                            <Typography
                              sx={{
                                fontSize: '1rem',
                                fontWeight: 600,
                                color: '#374151',
                              }}
                            >
                              +91
                            </Typography>
                          </Box>
                        </InputAdornment>
                      ),
                      endAdornment: phone && (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setPhone('')} size="small">
                            <Clear sx={{ fontSize: 18 }} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: '#f9fafb',
                        '& fieldset': {
                          borderColor: '#e5e7eb',
                          borderWidth: 2,
                        },
                        '&:hover fieldset': { borderColor: '#d1d5db' },
                        '&.Mui-focused fieldset': { borderColor: '#000000' },
                      },
                    }}
                  />

                  {currentErrorMessage && step === 'phone' && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                      {currentErrorMessage}
                    </Alert>
                  )}

                  {showAdminBypassOption && (
                    <Button
                      fullWidth
                      variant="outlined"
                      size="large"
                      onClick={() => {
                        setOtpMode('bypass');
                        setStep('otp');
                        setResendTimer(0);
                        setIsTimerActive(false);
                      }}
                      sx={{
                        mb: 2,
                        borderRadius: 2,
                        borderColor: 'rgba(255,255,255,0.45)',
                        color: '#000',
                        textTransform: 'none',
                      }}
                    >
                      Use Admin Bypass (Enter PIN)
                    </Button>
                  )}

                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleSendOTP}
                    disabled={!isPhoneValid || isSending}
                    endIcon={isSending ? null : <ArrowForward />}
                    sx={{
                      py: 1.75,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #000000 0%, #2d2d2d 100%)',
                      fontWeight: 700,
                      fontSize: '1rem',
                      textTransform: 'none',
                      boxShadow: '0 4px 14px rgba(0, 0, 0, 0.25)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #1a1a1a 0%, #333333 100%)',
                        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.35)',
                      },
                      '&:disabled': {
                        bgcolor: '#e5e7eb',
                        color: '#9ca3af',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    {isSending ? (
                      <CircularProgress size={24} sx={{ color: '#9ca3af' }} />
                    ) : (
                      t('auth.getOtp')
                    )}
                  </Button>

                  <Typography
                    sx={{
                      fontSize: '0.8rem',
                      color: '#9ca3af',
                      textAlign: 'center',
                      mt: 3,
                    }}
                  >
                    {t('auth.termsAgree')}{' '}
                    <Box
                      component="span"
                      sx={{ color: '#000000', cursor: 'pointer' }}
                      onClick={() => router.push('/terms')}
                    >
                      {t('auth.terms')}
                    </Box>{' '}
                    {t('auth.and')}{' '}
                    <Box component="span" sx={{ color: '#000000', cursor: 'pointer' }}>
                      {t('auth.privacyPolicy')}
                    </Box>
                  </Typography>
                </Box>
              </Slide>

              <Slide direction="left" in={step === 'otp'} mountOnEnter unmountOnExit>
                <Box>
                  <Typography
                    sx={{
                      fontSize: '1.75rem',
                      fontWeight: 700,
                      color: '#0f172a',
                      mb: 1,
                    }}
                  >
                    {otpMode === 'bypass' ? 'Enter Admin PIN' : t('auth.verifyNumber')}
                  </Typography>
                  <Typography sx={{ fontSize: '1rem', color: '#6b7280', mb: 4 }}>
                    {otpMode === 'bypass'
                      ? 'Enter the 6-digit code provided by your admin.'
                      : t('auth.otpSent', { phone })}
                    <Button
                      size="small"
                      onClick={handleChangePhone}
                      sx={{
                        ml: 1,
                        minWidth: 'auto',
                        p: 0,
                        textTransform: 'none',
                        fontWeight: 600,
                        color: '#000000',
                        '&:hover': {
                          bgcolor: 'transparent',
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {t('auth.edit')}
                    </Button>
                  </Typography>

                  {/* OTP Inputs */}
                  <Stack
                    direction="row"
                    spacing={1.5}
                    sx={{ mb: 3, justifyContent: 'center' }}
                    onPaste={handleOtpPaste}
                  >
                    {otp.map((digit, index) => (
                      <TextField
                        key={index}
                        inputRef={(el) => (otpRefs.current[index] = el)}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        inputProps={{
                          maxLength: 1,
                          inputMode: 'numeric',
                          style: {
                            textAlign: 'center',
                            fontSize: '1.5rem',
                            fontWeight: 700,
                            padding: '14px 0',
                          },
                        }}
                        disabled={isVerifying}
                        autoFocus={index === 0}
                        sx={{
                          width: 56,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            bgcolor: digit ? alpha('#000000', 0.08) : '#f9fafb',
                            transition: 'all 0.2s ease',
                            '& fieldset': {
                              borderColor: digit ? '#000000' : '#e5e7eb',
                              borderWidth: 2,
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#000000',
                            },
                          },
                        }}
                      />
                    ))}
                  </Stack>

                  {currentErrorMessage && step === 'otp' && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                      {currentErrorMessage}
                    </Alert>
                  )}

                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleVerifyOTP}
                    disabled={isVerifying || otp.join('').length !== 6}
                    sx={{
                      py: 1.75,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #000000 0%, #2d2d2d 100%)',
                      fontWeight: 700,
                      fontSize: '1rem',
                      textTransform: 'none',
                      boxShadow: '0 4px 14px rgba(1, 97, 31, 0.25)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #1a1a1a 0%, #333333 100%)',
                      },
                      '&:disabled': {
                        bgcolor: '#e5e7eb',
                        color: '#9ca3af',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    {isVerifying ? (
                      <CircularProgress size={24} sx={{ color: '#9ca3af' }} />
                    ) : (
                      t('auth.verifyContinue')
                    )}
                  </Button>

                  <Box sx={{ textAlign: 'center', mt: 3 }}>
                    {isTimerActive ? (
                      <Typography sx={{ fontSize: '0.9rem', color: '#6b7280' }}>
                        {t('auth.resendIn')}{' '}
                        <Box component="span" sx={{ fontWeight: 700, color: '#000' }}>
                          {Math.floor(resendTimer / 60)}:
                          {(resendTimer % 60).toString().padStart(2, '0')}
                        </Box>
                      </Typography>
                    ) : (
                      <Button
                        variant="text"
                        onClick={handleResendOTP}
                        disabled={isResending}
                        sx={{
                          fontWeight: 600,
                          color: '#000000',
                          textTransform: 'none',
                          '&:hover': { bgcolor: alpha('#000000', 0.05) },
                        }}
                      >
                        {isResending ? t('auth.sending') : t('auth.resendOtp')}
                      </Button>
                    )}
                  </Box>
                </Box>
              </Slide>
            </Box>
          </Box>
        </Box>
      </>
    );
  }

  // Mobile Layout
  return (
    <>
      <SimpleNav />
      {/* reCAPTCHA container for Firebase Phone Auth (invisible) */}
      <div id="recaptcha-container"></div>
      <Box
        sx={{
          height: '100vh',
          maxHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          background: 'linear-gradient(180deg, #000000 0%, #1a1a1a 40%, #2d2d2d 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Animated background */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            overflow: 'hidden',
            pointerEvents: 'none',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: -80,
              right: -80,
              width: 250,
              height: 250,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
              animation: 'pulse 4s ease-in-out infinite',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '30%',
              left: -60,
              width: 180,
              height: 180,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%)',
            }}
          />
        </Box>

        {/* Header with back button */}
        <Box sx={{ p: 2, position: 'relative', zIndex: 1 }}>
          {step === 'otp' && (
            <IconButton
              onClick={() => {
                setStep('phone');
                setOtp(['', '', '', '', '', '']);
              }}
              sx={{
                color: '#fff',
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
              }}
            >
              <ArrowBack />
            </IconButton>
          )}
        </Box>

        {/* Logo & Branding */}
        <Box
          sx={{
            textAlign: 'center',
            pt: { xs: 6, sm: 8 },
            pb: { xs: 3, sm: 4 },
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Fade in timeout={600}>
            <Box sx={{ textAlign: 'center', px: 2, maxWidth: 360, mx: 'auto' }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 64,
                  height: 64,
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)',
                  mb: 2,
                  animation: 'float 3s ease-in-out infinite',
                  '@keyframes float': {
                    '0%, 100%': { transform: 'translateY(0)' },
                    '50%': { transform: 'translateY(-6px)' },
                  },
                }}
              >
                <Image
                  src="/assets/terrakite-logo.png"
                  alt={brandingConfig.brandName}
                  width={40}
                  height={40}
                  style={{ objectFit: 'contain' }}
                />
              </Box>
              <Typography
                sx={{
                  fontSize: { xs: '1.25rem', sm: '1.4rem' },
                  fontWeight: 800,
                  color: '#fff',
                  letterSpacing: 2,
                  lineHeight: 1.1,
                  textTransform: 'uppercase',
                }}
              >
                {brandingConfig.brandName}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                  color: 'rgba(255,255,255,0.8)',
                  mt: 1,
                  lineHeight: 1.35,
                }}
              >
                {t('auth.taglineMobile')}
              </Typography>

              {/* <Stack
                direction="row"
                spacing={1}
                justifyContent="center"
                flexWrap="wrap"
                sx={{ mt: 2 }}
              >
                {features.slice(0, 3).map((feature) => (
                  <Chip
                    key={feature.titleKey}
                    label={t(feature.titleKey)}
                    icon={<feature.icon sx={{ fontSize: 18, color: '#fff' }} />}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.12)',
                      color: '#fff',
                      border: '1px solid rgba(255,255,255,0.2)',
                      fontWeight: 600,
                      px: 1.5,
                    }}
                  />
                ))}
              </Stack> */}
            </Box>
          </Fade>
        </Box>

        {/* Main Content */}
        <Box
          sx={{
            flex: 1,
            bgcolor: '#fff',
            borderTopLeftRadius: 32,
            borderTopRightRadius: 32,
            px: 3,
            pt: 4,
            pb: 4,
            position: 'relative',
            zIndex: 1,
            boxShadow: '0 -10px 40px rgba(0,0,0,0.1)',
          }}
        >
          {step === 'phone' && (
            <Slide direction="up" in timeout={400}>
              <Box>
                <Typography
                  sx={{
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    color: '#0f172a',
                    mb: 0.5,
                  }}
                >
                  {t('auth.getStarted')}
                </Typography>
                <Typography sx={{ fontSize: '0.95rem', color: '#6b7280', mb: 3 }}>
                  {t('auth.enterMobileContinue')}
                </Typography>

                {/* Phone Input */}
                <TextField
                  fullWidth
                  placeholder={t('auth.phonePlaceholder')}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                  inputProps={{
                    maxLength: 10,
                    inputMode: 'numeric',
                    style: {
                      fontSize: '1.1rem',
                      fontWeight: 500,
                      letterSpacing: 0.5,
                    },
                  }}
                  disabled={isSending}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            pr: 1.5,
                            borderRight: '1px solid #e5e7eb',
                          }}
                        >
                          <Typography sx={{ fontSize: '1.1rem' }}>🇮🇳</Typography>
                          <Typography
                            sx={{
                              fontSize: '0.95rem',
                              fontWeight: 600,
                              color: '#374151',
                            }}
                          >
                            +91
                          </Typography>
                        </Box>
                      </InputAdornment>
                    ),
                    endAdornment: phone && (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setPhone('')} size="small">
                          <Clear sx={{ fontSize: 18 }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 2.5,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2.5,
                      bgcolor: '#f9fafb',
                      '& fieldset': { borderColor: '#e5e7eb', borderWidth: 2 },
                      '&:hover fieldset': { borderColor: '#d1d5db' },
                      '&.Mui-focused fieldset': { borderColor: '#000000' },
                    },
                  }}
                />

                {currentErrorMessage && step === 'phone' && (
                  <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                    {currentErrorMessage}
                  </Alert>
                )}

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleSendOTP}
                  disabled={!isPhoneValid || isSending}
                  endIcon={isSending ? null : <ArrowForward />}
                  sx={{
                    py: 1.75,
                    borderRadius: 2.5,
                    background: 'linear-gradient(135deg, #000000 0%, #2d2d2d 100%)',
                    fontWeight: 700,
                    fontSize: '1rem',
                    textTransform: 'none',
                    boxShadow: '0 4px 14px rgba(1, 97, 31, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1a1a1a 0%, #333333 100%)',
                    },
                    '&:disabled': {
                      bgcolor: '#e5e7eb',
                      color: '#9ca3af',
                      boxShadow: 'none',
                    },
                  }}
                >
                  {isSending ? (
                    <CircularProgress size={24} sx={{ color: '#9ca3af' }} />
                  ) : (
                    t('common.continue')
                  )}
                </Button>

                {/* Features - Mobile */}
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    mt: 4,
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  {[
                    { icon: <Speed sx={{ fontSize: 14 }} />, label: t('auth.chips.fast') },
                    {
                      icon: <Security sx={{ fontSize: 14 }} />,
                      label: t('auth.chips.secure'),
                    },
                    {
                      icon: <Verified sx={{ fontSize: 14 }} />,
                      label: t('auth.chips.trusted'),
                    },
                  ].map((item, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 2,
                        bgcolor: '#f0fdf4',
                        color: '#15803d',
                      }}
                    >
                      {item.icon}
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                        {item.label}
                      </Typography>
                    </Box>
                  ))}
                </Stack>

                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    color: '#9ca3af',
                    textAlign: 'center',
                    mt: 3,
                    lineHeight: 1.6,
                  }}
                >
                  {t('auth.termsAgreementFull')}
                </Typography>
              </Box>
            </Slide>
          )}

          {step === 'otp' && (
            <Slide direction="left" in timeout={400}>
              <Box>
                <Typography
                  sx={{
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    color: '#0f172a',
                    mb: 0.5,
                  }}
                >
                  {t('auth.verifyOtp')}
                </Typography>
                <Typography sx={{ fontSize: '0.95rem', color: '#6b7280', mb: 3 }}>
                  {t('auth.otpSent', { phone })}
                  <Button
                    size="small"
                    onClick={handleChangePhone}
                    sx={{
                      ml: 1,
                      minWidth: 'auto',
                      p: 0,
                      textTransform: 'none',
                      fontWeight: 600,
                      color: '#000000',
                      '&:hover': {
                        bgcolor: 'transparent',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    {t('auth.edit')}
                  </Button>
                </Typography>

                {/* OTP Input */}
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mb: 3, justifyContent: 'center' }}
                  onPaste={handleOtpPaste}
                >
                  {otp.map((digit, index) => (
                    <TextField
                      key={index}
                      inputRef={(el) => (otpRefs.current[index] = el)}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      inputProps={{
                        maxLength: 1,
                        inputMode: 'numeric',
                        style: {
                          textAlign: 'center',
                          fontSize: '1.4rem',
                          fontWeight: 700,
                          padding: '12px 0',
                        },
                      }}
                      disabled={isVerifying}
                      autoFocus={index === 0}
                      sx={{
                        width: 48,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: digit ? alpha('#000000', 0.08) : '#f9fafb',
                          transition: 'all 0.2s ease',
                          '& fieldset': {
                            borderColor: digit ? '#000000' : '#e5e7eb',
                            borderWidth: 2,
                          },
                          '&.Mui-focused fieldset': { borderColor: '#000000' },
                        },
                      }}
                    />
                  ))}
                </Stack>

                {currentErrorMessage && step === 'otp' && (
                  <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                    {currentErrorMessage}
                  </Alert>
                )}

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleVerifyOTP}
                  disabled={isVerifying || otp.join('').length !== 6}
                  sx={{
                    py: 1.75,
                    borderRadius: 2.5,
                    background: 'linear-gradient(135deg, #000000 0%, #2d2d2d 100%)',
                    fontWeight: 700,
                    fontSize: '1rem',
                    textTransform: 'none',
                    boxShadow: '0 4px 14px rgba(1, 97, 31, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1a1a1a 0%, #333333 100%)',
                    },
                    '&:disabled': {
                      bgcolor: '#e5e7eb',
                      color: '#9ca3af',
                      boxShadow: 'none',
                    },
                  }}
                >
                  {isVerifying ? (
                    <CircularProgress size={24} sx={{ color: '#9ca3af' }} />
                  ) : (
                    t('auth.verify')
                  )}
                </Button>

                <Box sx={{ textAlign: 'center', mt: 3 }}>
                  {isTimerActive ? (
                    <Typography sx={{ fontSize: '0.9rem', color: '#6b7280' }}>
                      {t('auth.resendIn')}{' '}
                      <Box component="span" sx={{ fontWeight: 700, color: '#000' }}>
                        {Math.floor(resendTimer / 60)}:
                        {(resendTimer % 60).toString().padStart(2, '0')}
                      </Box>
                    </Typography>
                  ) : (
                    <Button
                      variant="text"
                      onClick={handleResendOTP}
                      disabled={isResending}
                      sx={{
                        fontWeight: 600,
                        color: '#000000',
                        textTransform: 'none',
                        '&:hover': { bgcolor: alpha('#000000', 0.05) },
                      }}
                    >
                      {isResending ? t('auth.sending') : t('auth.resendCode')}
                    </Button>
                  )}
                </Box>
              </Box>
            </Slide>
          )}
        </Box>
      </Box>{' '}
    </>
  );
};

export default Login;
